import type { ImmobilizedStockSummary } from "#/types/report";
import { formatCurrency } from "#/utils/formatCurrency";
import { IconArrowUpRight } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReportSection from "./ReportSection";

interface ImmobilizedStockSectionProps {
  data: ImmobilizedStockSummary;
  tooltip?: ReactNode;
}

/** Items list pre-filtered to stuck stock, by the same rule this card uses. */
const IMMOBILIZED_ITEMS_URL = "/inventory/items?immobilized=true";

const ImmobilizedStockSection = ({ data, tooltip }: ImmobilizedStockSectionProps) => {
  const { t } = useTranslation();

  const tiles = [
    { label: t("reports.inventoryScan.immobilized.items"), value: data.itemCount.toLocaleString() },
    { label: t("reports.inventoryScan.immobilized.categories"), value: data.categoryCount.toLocaleString() },
    { label: t("reports.inventoryScan.immobilized.sites"), value: data.siteCount.toLocaleString() },
    { label: t("reports.inventoryScan.immobilized.value"), value: formatCurrency(data.totalValue) },
  ];

  return (
    <ReportSection
      title={t("reports.inventoryScan.immobilized.title")}
      subtitle={t("reports.inventoryScan.immobilized.subtitle")}
      tooltip={tooltip}
    >
      {data.itemCount > 0 ? (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {tiles.map((tile) => (
              <a
                key={tile.label}
                href={IMMOBILIZED_ITEMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                title={t("reports.inventoryScan.immobilized.openList")}
                aria-label={`${tile.label}: ${tile.value}. ${t("reports.inventoryScan.immobilized.openList")}`}
                className="group relative flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 p-4 no-underline transition-colors hover:border-red-400 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500"
              >
                <IconArrowUpRight
                  size={16}
                  className="absolute right-3 top-3 text-red-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-red-600"
                />
                <span className="pr-6 text-xs font-semibold uppercase tracking-wide text-red-700">
                  {tile.label}
                </span>
                <span className="text-2xl font-extrabold text-red-600">{tile.value}</span>
              </a>
            ))}
          </div>
          {data.siteNames.length > 0 && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">{t("reports.inventoryScan.immobilized.locations")}:</span>{" "}
              {data.siteNames.join(", ")}
            </div>
          )}
        </div>
      ) : (
        <span className="text-sm text-gray-500">
          {t("reports.inventoryScan.immobilized.none")}
        </span>
      )}
    </ReportSection>
  );
};

export default ImmobilizedStockSection;
